import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Building2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";

interface Organization {
  organization_id: string;
  organization_name: string;
  logo_url?: string | null;
  created_at: string;
  updated_at: string;
}

const OrganizationSelector = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchingFresh, setFetchingFresh] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [autoSelected, setAutoSelected] = useState(false);
  const { user, signOut } = useAuth();
  const { setOrganization } = useOrganization();
  const navigate = useNavigate();

  const fetchOrganizationsFromAPI = async () => {
    const userRole = await supabase.rpc('get_current_user_role').throwOnError();
    console.log('userRole', userRole);

    const { data: allOrgs, error: allOrgsError } = await supabase
      .from("organizations")
      .select("id, name, logo_url, created_at, updated_at");
    if (allOrgsError) throw allOrgsError;
    const orgs = (
      allOrgs?.map((org) => ({
        organization_id: org.id,
        organization_name: org.name,
        logo_url: org.logo_url,
        created_at: org.created_at,
        updated_at: org.updated_at,
      })) || []);
      console.log('got orgs', orgs);
      return orgs;

  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      let hadCached = false;
      try {
        // Show cached data immediately if available
        const cachedOrgs = localStorage.getItem("cachedOrganizations");
        if (cachedOrgs) {
          const parsedOrgs = JSON.parse(cachedOrgs);
          setOrganizations(parsedOrgs);
          setFilteredOrgs(parsedOrgs);
          setLoading(false); // Show UI, but still fetch fresh
          hadCached = true;
        }
        setFetchingFresh(true);
        // Always fetch fresh data in the background
        try {
          const freshData = await fetchOrganizationsFromAPI();
          setOrganizations(freshData);
          setFilteredOrgs(freshData);
          localStorage.setItem(
            "cachedOrganizations",
            JSON.stringify(freshData)
          );
        } catch (error) {
          if (!hadCached) {
            toast.error("Failed to load organizations");
          }
        }
        setFetchingFresh(false);
        setLoading(false); // In case there was no cached data
      } catch (error) {
        toast.error("Failed to load organizations");
        setFetchingFresh(false);
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, [user]);

  useEffect(() => {
    const filtered = organizations.filter((org) =>
      org.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOrgs(filtered);
  }, [searchTerm, organizations]);

  const handleSelectOrganization = async (orgId: string, orgName: string) => {
    setSelecting(true);
    try {
      // Find the full org object from organizations
      const org = organizations.find((o) => o.organization_id === orgId);
      if (!org) throw new Error("Organization not found");
      // Set organization using context (which will also update localStorage)
      await setOrganization({
        id: org.organization_id,
        name: org.organization_name,
        logo_url: org.logo_url,
        created_at: org.created_at,
        updated_at: org.updated_at,
      });
      toast.success("Organization selected successfully");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to select organization");
    } finally {
      setSelecting(false);
    }
  };

  // Auto-select the single available organization for non-admin users.
  // Run this once when data is ready, to avoid triggering state updates during render.
  useEffect(() => {
    if (
      !fetchingFresh &&
      organizations.length === 1 &&
      user?.role !== "admin" &&
      !autoSelected
    ) {
      setAutoSelected(true);
      const org = organizations[0];
      // Fire and forget; any errors are handled inside the function
      void handleSelectOrganization(org.organization_id, org.organization_name);
    }
  }, [fetchingFresh, organizations, user?.role, autoSelected]);

  const handleClose = () => {
    navigate("/dashboard");
  };

  // Show loading spinner if still loading and no cached data
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-glyco-purple"></div>
          <p className="mt-4 text-gray-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  // Show subtle loading indicator if fetching fresh in background
  const showBackgroundLoading = fetchingFresh;

  // While auto-selecting or user-initiated selecting, show a spinner
  if (selecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-glyco-purple"></div>
          <p className="mt-4 text-gray-600">Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  // No organizations assigned (only for non-admin users)
  if (!fetchingFresh && organizations.length === 0 && user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <CardTitle className="text-xl">No Organization Access</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              Your account is not currently associated with any organization.
            </p>
            <p className="text-sm text-gray-500">
              If this is a mistake, please contact support for assistance.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-glyco-purple">
              <Mail className="h-4 w-4" />
              <a
                href="mailto:info@tastermonial.com"
                className="hover:underline"
              >
                info@tastermonial.com
              </a>
            </div>
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full mt-4"
            >
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No organizations for admin users - show error message
  if (!fetchingFresh && organizations.length === 0 && user?.role === "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <CardTitle className="text-xl">No Organizations Found</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              No organizations exist in the system yet.
            </p>
            <p className="text-sm text-gray-500">
              As an admin, you can create organizations in the system.
            </p>
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full mt-4"
            >
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Multiple organizations or switcher mode or admin - show selector
  return (
    <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center relative">
          <Building2 className="h-12 w-12 mx-auto text-glyco-purple mb-4" />
          <CardTitle className="text-xl">
            Select Organization
          </CardTitle>
          <p className="text-sm text-gray-600">
            {user?.role === "admin"
              ? "Choose any organization to access"
              : "Choose which organization you'd like to access"}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="search">Search Organizations</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                type="text"
                placeholder="Type to filter..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredOrgs.map((org) => (
              <Button
                key={org.organization_id}
                variant="outline"
                className="w-full justify-start text-left h-auto p-3"
                onClick={() =>
                  handleSelectOrganization(
                    org.organization_id,
                    org.organization_name
                  )
                }
                disabled={selecting}
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-glyco-purple" />
                  <div>
                    <p className="font-medium">{org.organization_name}</p>
                    <p className="text-xs text-gray-500">
                      Created {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </Button>
            ))}
            {showBackgroundLoading && (
              <div className="flex items-center justify-center py-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-glyco-purple mr-2"></div>
                <span className="text-xs text-gray-500">
                  Refreshing organizations...
                </span>
              </div>
            )}
          </div>

          {filteredOrgs.length === 0 && searchTerm && (
            <div className="text-center py-4 text-gray-500">
              <p>No organizations found matching "{searchTerm}"</p>
            </div>
          )}
          <Button
            variant="secondary"
            onClick={async () => {
              await signOut();
              navigate("/login");
            }}
            className="w-full mt-2"
          >
            Logout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationSelector;
